import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./headerV2.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { FAVORITES_MENU_IDENTIFIER } from '~/components/ace/favorites/favorites';
import { USER_MENU_IDENTIFIER } from '~/components/ace/userMenu/userMenu';
import { createEvent } from '~/modules/analytics/analyticsHelper';
import { storeAuthenticationContext } from '~/modules/analytics/authenticationContext';
import { ElementType } from '~/modules/analytics/gtmEventTypes/constants';
import { APPX_JP_SITE_URL, APPX_SITE_URL, IS_DEVELOPMENT, TBID_MOCK_USER } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { getOrgEditionBadgeTheme, getOrgName, hasOrgDomain, hasOrgEdition } from '~/modules/helpers/orgCardFields';
import { areSameOrgIds, getUpdatedSelectedOrg, getValidatedSelectedOrg, isStaleSelectedOrg } from '~/modules/helpers/orgContextHelper';
import { withLocalePrefix } from '~/modules/helpers/localeHelper';
import { switchToOrgPicker } from '~/modules/helpers/orgSwitchDropOff';
import { generateIisLoginUrl, generateIisSignupUrl, generateTbidLoginUrl, generateTbidSignUpUrl, login as tbidLogin, signup as tbidSignup } from '~/modules/helpers/routingHelper';
import { WireI18n, i18nStore } from '~/modules/stores/i18nStore';
import { orgContextStore, selectSelectedOrg } from '~/modules/stores/orgContextStore';
import { orgPickerStore, selectOrgPickerResolved } from '~/modules/stores/orgPickerStore';
import { Routes } from '~/modules/routes';
import { navigate, routerStore, selectUrl } from '~/modules/stores/routerStore';
import { selectIsAgentExchange, siteConfigStore } from '~/modules/stores/siteConfigStore';
import { selectIsAuthenticated, userStore } from '~/modules/stores/userStore';
import { featureFlagsStore, selectFeatureFlagsLoaded } from '~/modules/stores/feature_flags_store';
import { sendNotification } from '~/modules/utilities/notificationUtilities';
import { ENGLISH_LANGUAGE_CODE, JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { parseJson } from '~/modules/utilities/object_utils';
import { isOrgPickerEnabled, ORG_PICKER_PILOT_CHANGE_EVENT, ORG_PICKER_PILOT_KEY } from '~/modules/utilities/orgPickerPilot';
import { createHeaderV2Navigation } from './headerV2.navigation';
const MOBILE_NAV_MAX_WIDTH = 767;
const DEFAULT_TIER_HEIGHT = 56;
const MOBILE_UTILITY_IDS = {
  FAVORITES: 'favorites',
  HELP_CENTER: 'help-center',
  LOGIN: 'login',
  SIGNUP: 'signup',
  USER_MENU: 'user-menu'
};
const isRouteOrDescendant = (pathname, route) => pathname === route || pathname.startsWith(`${route}/`);
class HeaderV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    this.expandedHeaderHeight = DEFAULT_TIER_HEIGHT * 2;
    this.activeCollectionMenuId = 'business-needs';
    this.activeNavigationItemId = '';
    this.currentUrl = window.location.pathname;
    this.isLoggedIn = selectIsAuthenticated(userStore.getState());
    this.isAuthLoading = userStore.getState().isLoading;
    this.hasOrgSession = userStore.getState().isGuestUser === false;
    this.userToken = userStore.getState().userToken;
    this.flagUserToken = featureFlagsStore.getState().context?.userId;
    this.flagsResolved = selectFeatureFlagsLoaded(featureFlagsStore.getState());
    this.isAgentExchange = selectIsAgentExchange(siteConfigStore.getState());
    this.tbidProfile = userStore.getState().tbidProfile;
    this.selectedMobileUtilityPane = undefined;
    this.orgPickerEnabled = this.hasOrgSession && isOrgPickerEnabled();
    this.orgCount = (() => {
      const {
        totalCount,
        orgs
      } = orgPickerStore.getState();
      return Math.max(totalCount, orgs.length);
    })();
    this.selectedOrg = null;
    this.staleSelectionClearedOrgId = null;
    this.orgPickerResolved = selectOrgPickerResolved(orgPickerStore.getState());
    this.handleOrgPickerPilotChange = () => {
      this.refreshOrgPickerEnabled();
      this.reconcileMobileUtilityDetail();
    };
    this.handleOrgPickerPilotStorageChange = event => {
      if (event.storageArea === localStorage && (event.key === ORG_PICKER_PILOT_KEY || event.key === null)) {
        this.refreshOrgPickerEnabled();
        this.reconcileMobileUtilityDetail();
      }
    };
    this.publishHeaderHeight = () => {
      if (!this.headerElement) {
        return;
      }
      const isDesktop = window.innerWidth > MOBILE_NAV_MAX_WIDTH;
      const measuredHeight = this.headerElement.getBoundingClientRect().height;
      if (isDesktop && measuredHeight > 0) {
        this.expandedHeaderHeight = measuredHeight;
      }
      const isSimplified = this.headerElement.hasAttribute('data-simplified');
      const height = !isDesktop && measuredHeight > 0 ? measuredHeight : isDesktop && !isSimplified ? this.expandedHeaderHeight : this.expandedHeaderHeight / 2;
      document.body.style.setProperty('--ace-header-height', `${Math.round(height)}px`);
      document.body.style.setProperty('--ace-container-vertical-offset', '0px');
    };
  }
  setUserContext({
    isAuthenticated,
    hasOrgSession,
    isLoading,
    userToken,
    tbidProfile
  }) {
    this.isLoggedIn = isAuthenticated;
    this.hasOrgSession = hasOrgSession;
    this.isAuthLoading = isLoading;
    this.userToken = userToken;
    this.tbidProfile = tbidProfile;
    this.refreshOrgPickerEnabled();
  }
  setIsAgentExchange(isAgentExchange) {
    this.isAgentExchange = isAgentExchange;
  }
  setOrgPickerFeatureFlag({
    flagsResolved,
    flagUserToken
  }) {
    this.flagsResolved = flagsResolved;
    this.flagUserToken = flagUserToken;
    this.refreshOrgPickerEnabled();
    this.reconcileMobileUtilityDetail();
  }
  setOrgCount(orgCount) {
    this.orgCount = orgCount;
    this.orgPickerResolved = selectOrgPickerResolved(orgPickerStore.getState());
    this.reconcileMobileUtilityDetail();
  }
  setSelectedOrg(selectedOrg) {
    if (this.selectedOrg?.orgId !== selectedOrg?.orgId) {
      this.staleSelectionClearedOrgId = null;
    }
    this.selectedOrg = selectedOrg;
    this.reconcileSelectedOrg();
  }
  setCurrentUrl(url) {
    this.currentUrl = url || window.location.pathname;
  }
  setLabels(labels) {
    this.labels = labels;
  }
  setStatusBadgeLabels(statusBadgeLabels) {
    this.statusBadgeLabels = statusBadgeLabels;
  }
  setNotificationLabels(notifications) {
    this.notificationLabels = notifications;
  }
  setOrgContextMenuLabels(orgContextMenu) {
    this.orgContextMenuLabels = orgContextMenu;
  }
  get homeUrl() {
    return withLocalePrefix('/');
  }
  get navItems() {
    return this.navigation.navItems;
  }
  get l2Content() {
    return this.navigation.l2Content;
  }
  get collections() {
    return this.navigation.collections[this.activeCollectionMenuId];
  }
  get navigation() {
    const homeUrl = this.homeUrl;
    const navigationLabels = this.labels.unifiedHeaderV2;
    if (!this.cachedNavigation || this.cachedNavigationHomeUrl !== homeUrl || this.cachedNavigationLabels !== navigationLabels || this.cachedNavigationStatusBadgeLabels !== this.statusBadgeLabels || this.cachedNavigationIsAgentExchange !== this.isAgentExchange) {
      this.cachedNavigation = createHeaderV2Navigation(homeUrl, navigationLabels, this.isAgentExchange, this.statusBadgeLabels);
      this.cachedNavigationHomeUrl = homeUrl;
      this.cachedNavigationLabels = navigationLabels;
      this.cachedNavigationStatusBadgeLabels = this.statusBadgeLabels;
      this.cachedNavigationIsAgentExchange = this.isAgentExchange;
    }
    return this.cachedNavigation;
  }
  get activeItemId() {
    const currentUrl = this.currentUrl.startsWith('/') || /^[a-z][a-z\d+.-]*:/i.test(this.currentUrl) ? this.currentUrl : `/${this.currentUrl}`;
    const pathname = new URL(currentUrl, window.location.origin).pathname.replace(/^\/jp(?=\/|$)/, '');
    if (isRouteOrDescendant(pathname, '/explore/agentforce')) {
      return 'products';
    }
    if (this.isAgentExchange) {
      if (isRouteOrDescendant(pathname, `/${Routes.SKILLS}`)) {
        return 'solutions';
      }
      if (isRouteOrDescendant(pathname, `/${Routes.COLLECTIONS_HOME}`)) {
        return 'collections';
      }
    }
    if (isRouteOrDescendant(pathname, '/explore/business-needs')) {
      return 'business-needs';
    }
    if (isRouteOrDescendant(pathname, '/explore/products')) {
      return 'products';
    }
    if (isRouteOrDescendant(pathname, `/${Routes.CONSULTING_HOME}`)) {
      return 'consultants';
    }
    if (isRouteOrDescendant(pathname, `/${Routes.LEARN_HOME}`) || isRouteOrDescendant(pathname, '/mktcollections/curated/appexchange_contents')) {
      return 'learn';
    }
    return '';
  }
  get unifiedHeaderClass() {
    if (this.activeNavigationItemId === 'products') {
      return 'products-menu-open';
    }
    if (this.activeNavigationItemId === 'solutions') {
      return 'solutions-menu-open';
    }
    return '';
  }
  get showOrgContextMenu() {
    return this.showAuthenticatedUtilities && this.orgPickerEnabled && this.orgPickerResolved && this.orgCount > 1;
  }
  get showAccountLoading() {
    if (this.isAuthLoading) {
      return true;
    }
    if (!this.isLoggedIn || !this.hasOrgSession || getUserLanguage() !== ENGLISH_LANGUAGE_CODE) {
      return false;
    }
    return this.isOrgPickerDecisionPending || this.orgPickerEnabled && !this.orgPickerResolved;
  }
  get isOrgPickerDecisionPending() {
    return this.hasOrgSession && getUserLanguage() === ENGLISH_LANGUAGE_CODE && (!this.flagsResolved || this.flagUserToken !== this.userToken);
  }
  get showAuthenticatedUtilities() {
    return !this.showAccountLoading && this.isLoggedIn;
  }
  get showOrgCard() {
    return this.showOrgContextMenu && Boolean(getValidatedSelectedOrg(this.selectedOrg, orgPickerStore.getState()));
  }
  get orgName() {
    return getOrgName(this.selectedOrg);
  }
  get hasEdition() {
    return hasOrgEdition(this.selectedOrg);
  }
  get hasDomain() {
    return hasOrgDomain(this.selectedOrg);
  }
  get editionBadgeTheme() {
    return getOrgEditionBadgeTheme(this.selectedOrg);
  }
  get switchOrgLabel() {
    return this.orgContextMenuLabels?.switchOrg ?? '';
  }
  get selectOrgLabel() {
    return this.orgContextMenuLabels?.selectOrg ?? '';
  }
  get orgIconDescription() {
    return this.orgContextMenuLabels?.orgIconDescription ?? '';
  }
  get mobileUserMenuLabel() {
    if (this.tbidProfile?.active) {
      return i18nStore.getState().i18n?.shared?.userName?.({
        FIRST_NAME: this.tbidProfile.given_name,
        LAST_NAME: this.tbidProfile.family_name
      }) ?? this.labels?.userMenuLabel;
    }
    return this.labels?.userMenuLabel;
  }
  get favoritesHref() {
    const path = `/${Routes.FAVORITE_LISTINGS}`;
    const appExchangeBaseUrl = getUserLanguage() === JAPANESE_LANGUAGE_CODE ? APPX_JP_SITE_URL : APPX_SITE_URL;
    return this.isAgentExchange ? `${appExchangeBaseUrl}${path}` : path;
  }
  get favoritesLabel() {
    return this.labels?.favoritesAltText;
  }
  get helpCenterTitle() {
    return this.labels?.helpCenter?.title;
  }
  get mobileUtilityDetails() {
    const key = JSON.stringify([this.isLoggedIn, this.labels.myAccount, this.helpCenterTitle]);
    if (!this.cachedMobileUtilityDetails || this.cachedMobileUtilityDetailsKey !== key) {
      this.cachedMobileUtilityDetails = [...(this.isLoggedIn ? [{
        id: MOBILE_UTILITY_IDS.USER_MENU,
        title: this.labels.myAccount
      }] : []), {
        id: MOBILE_UTILITY_IDS.HELP_CENTER,
        title: this.helpCenterTitle
      }];
      this.cachedMobileUtilityDetailsKey = key;
    }
    return this.cachedMobileUtilityDetails;
  }
  get mobileUtilityItems() {
    const helpCenter = {
      id: MOBILE_UTILITY_IDS.HELP_CENTER,
      label: this.helpCenterTitle,
      detailId: MOBILE_UTILITY_IDS.HELP_CENTER,
      icon: {
        kind: 'ax-icon',
        name: 'help-filled',
        spriteUrl: '/images/icons.svg'
      }
    };
    if (this.showAccountLoading) {
      return [helpCenter];
    }
    if (!this.isLoggedIn) {
      const loginHref = FeatureFlags.FF_IIS_JS_V2 ? generateIisLoginUrl() : generateTbidLoginUrl();
      const signupHref = FeatureFlags.FF_IIS_JS_V2 ? generateIisSignupUrl() : generateTbidSignUpUrl();
      const key = JSON.stringify(['guest', this.labels.login, this.labels.signup, this.helpCenterTitle, loginHref, signupHref]);
      if (!this.cachedMobileUtilityItems || this.cachedMobileUtilityItemsKey !== key) {
        this.cachedMobileUtilityItems = [{
          id: MOBILE_UTILITY_IDS.LOGIN,
          label: this.labels.login,
          href: loginHref,
          target: '_self',
          icon: {
            kind: 'ax-icon',
            name: 'user-full',
            spriteUrl: '/images/icons.svg'
          }
        }, {
          id: MOBILE_UTILITY_IDS.SIGNUP,
          label: this.labels.signup,
          href: signupHref,
          target: '_self',
          icon: {
            kind: 'ax-icon',
            name: 'edit',
            spriteUrl: '/images/icons.svg'
          }
        }, helpCenter];
        this.cachedMobileUtilityItemsKey = key;
      }
      return this.cachedMobileUtilityItems;
    }
    const key = JSON.stringify(['authenticated', this.mobileUserMenuLabel, this.favoritesLabel, this.favoritesHref, this.helpCenterTitle]);
    if (!this.cachedMobileUtilityItems || this.cachedMobileUtilityItemsKey !== key) {
      this.cachedMobileUtilityItems = [{
        id: MOBILE_UTILITY_IDS.USER_MENU,
        label: this.mobileUserMenuLabel,
        detailId: MOBILE_UTILITY_IDS.USER_MENU,
        icon: {
          kind: 'ax-icon',
          name: 'user-full',
          spriteUrl: '/images/icons.svg'
        }
      }, {
        id: MOBILE_UTILITY_IDS.FAVORITES,
        label: this.favoritesLabel,
        href: this.favoritesHref,
        target: '_self',
        icon: {
          kind: 'ax-icon',
          name: 'favorite-heart',
          spriteUrl: '/images/icons.svg'
        }
      }, helpCenter];
      this.cachedMobileUtilityItemsKey = key;
    }
    return this.cachedMobileUtilityItems;
  }
  get showMobileUserMenu() {
    return this.isLoggedIn && this.selectedMobileUtilityPane === MOBILE_UTILITY_IDS.USER_MENU;
  }
  get showMobileHelpCenter() {
    return this.selectedMobileUtilityPane === MOBILE_UTILITY_IDS.HELP_CENTER;
  }
  reconcileMobileUtilityDetail() {
    if (this.selectedMobileUtilityPane !== MOBILE_UTILITY_IDS.USER_MENU || this.isLoggedIn) {
      return;
    }
    // The Design System owns the focus-restoring close behavior for its slotted mobile detail.
    this.template?.querySelector('ace-user-menu-mobile')?.dispatchEvent(new CustomEvent('requestclose', {
      bubbles: true,
      composed: true
    }));
    this.selectedMobileUtilityPane = undefined;
  }
  refreshOrgPickerEnabled() {
    this.orgPickerEnabled = this.hasOrgSession && isOrgPickerEnabled();
    if (this.isLoggedIn && !this.isAuthLoading && !this.isOrgPickerDecisionPending && this.orgPickerEnabled) {
      const orgPickerState = orgPickerStore.getState();
      this.orgPickerResolved = selectOrgPickerResolved(orgPickerState);
      // A failed tenant request deliberately falls back to the standard utility cluster for
      // this session. Do not retry on later flag or pilot events, which would reintroduce a
      // show/hide transition after the fallback has rendered.
      if (!orgPickerState.error) {
        this.loadOrgCount();
      }
    }
  }
  async loadOrgCount() {
    await orgPickerStore.loadTenants();
    if (!this.isLoggedIn || !this.orgPickerEnabled) {
      return;
    }
    const {
      totalCount,
      orgs
    } = orgPickerStore.getState();
    this.orgPickerResolved = selectOrgPickerResolved(orgPickerStore.getState());
    if (!this.orgPickerResolved) {
      return;
    }
    this.orgCount = Math.max(totalCount, orgs.length);
    this.reconcileSelectedOrg();
  }
  reconcileSelectedOrg() {
    if (this.isAuthLoading || this.isOrgPickerDecisionPending) {
      return;
    }
    const {
      totalCount,
      orgs,
      hasLoaded
    } = orgPickerStore.getState();
    const selectedOrg = this.selectedOrg;
    if (!hasLoaded || !this.orgPickerEnabled || orgs.length !== totalCount || selectedOrg === null) {
      return;
    }
    if (isStaleSelectedOrg(selectedOrg, orgs)) {
      if (selectedOrg.orgId === this.staleSelectionClearedOrgId) {
        return;
      }
      this.staleSelectionClearedOrgId = selectedOrg.orgId;
      orgContextStore.clearSelectedOrg();
      sendNotification(this.notificationLabels?.orgPicker?.staleOrgCleared?.successMessage ?? '');
      return;
    }
    const updatedOrg = getUpdatedSelectedOrg(selectedOrg, orgs);
    if (updatedOrg) {
      orgContextStore.setSelectedOrg(updatedOrg);
    }
    const orgProfile = orgContextStore.getState().orgProfile;
    if (!orgProfile || !areSameOrgIds(orgProfile.orgId, selectedOrg.orgId)) {
      orgContextStore.loadOrgProfile(selectedOrg.orgId);
    }
  }
  login() {
    storeAuthenticationContext({
      origin: 'header'
    });
    if (IS_DEVELOPMENT && TBID_MOCK_USER) {
      userStore.setMockedUser(parseJson(TBID_MOCK_USER));
      return;
    }
    tbidLogin();
  }
  signup() {
    storeAuthenticationContext({
      origin: 'header'
    });
    tbidSignup();
  }
  dispatchMobileLoginNavLink(navLink) {
    this.dispatchEvent(new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        eventType: 'click',
        elementType: 'loginNavLink',
        navLink: {
          ...navLink,
          destinationType: this.isAgentExchange ? 'external' : 'internal',
          ctaClick: false
        }
      }
    }));
  }
  handleMobileFavoritesClick(event) {
    event.preventDefault();
    this.dispatchMobileLoginNavLink({
      menuItem: FAVORITES_MENU_IDENTIFIER,
      clickText: FAVORITES_MENU_IDENTIFIER,
      clickUrl: this.favoritesHref
    });
    if (this.isAgentExchange) {
      window.location.href = this.favoritesHref;
    } else {
      navigate(this.favoritesHref);
    }
  }
  handleMobileUtilityNavigate(event) {
    if (event.detail.id === MOBILE_UTILITY_IDS.LOGIN) {
      event.preventDefault();
      this.login();
    } else if (event.detail.id === MOBILE_UTILITY_IDS.SIGNUP) {
      event.preventDefault();
      this.signup();
    } else if (event.detail.id === MOBILE_UTILITY_IDS.FAVORITES) {
      this.handleMobileFavoritesClick(event);
    }
  }
  handleMobileUtilityDetailChange(event) {
    const id = event.detail.id;
    this.selectedMobileUtilityPane = id === MOBILE_UTILITY_IDS.USER_MENU || id === MOBILE_UTILITY_IDS.HELP_CENTER ? id : undefined;
    if (id === MOBILE_UTILITY_IDS.USER_MENU) {
      this.dispatchMobileLoginNavLink({
        menuItem: USER_MENU_IDENTIFIER,
        clickText: USER_MENU_IDENTIFIER,
        navLevel: 'l1'
      });
    }
  }
  handleSwitchOrg(event) {
    event.preventDefault();
    this.dispatchEvent(createEvent({
      eventType: 'click',
      elementType: this.showOrgCard ? ElementType.ORG_PICKER_SWITCH_ORG : ElementType.ORG_PICKER_BADGE,
      surface: 'mobile'
    }));
    switchToOrgPicker();
  }
  handleNavClick(event) {
    const {
      id,
      label,
      href,
      level
    } = event.detail;
    this.activeNavigationItemId = id;
    if (Object.prototype.hasOwnProperty.call(this.navigation.collections, id)) {
      this.activeCollectionMenuId = id;
    }
    this.dispatchNavAnalytics(label, href ?? '', level, label);
  }
  handleL2Navigate(event) {
    const {
      label,
      href,
      level,
      groupLabel
    } = event.detail;
    this.dispatchNavAnalytics(label, href, level, groupLabel ? `${groupLabel}:${label}` : label);
  }
  dispatchNavAnalytics(label, href, level, navItem) {
    this.dispatchEvent(new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        eventType: 'click',
        elementType: 'headerNavLink',
        headerNavLink: {
          label,
          url: href,
          level,
          navItem
        }
      }
    }));
  }
  renderedCallback() {
    const headerElement = this.template.querySelector('ax-unified-header-v2');
    if (!headerElement || headerElement === this.headerElement) {
      return;
    }
    this.headerResizeObserver?.disconnect();
    this.headerMutationObserver?.disconnect();
    this.headerElement = headerElement;
    this.publishHeaderHeight();
    this.headerResizeObserver = new ResizeObserver(() => this.publishHeaderHeight());
    this.headerResizeObserver.observe(headerElement);
    this.headerMutationObserver = new MutationObserver(() => this.publishHeaderHeight());
    this.headerMutationObserver.observe(headerElement, {
      attributes: true,
      attributeFilter: ['data-simplified']
    });
    window.addEventListener('resize', this.publishHeaderHeight);
  }
  connectedCallback() {
    window.addEventListener(ORG_PICKER_PILOT_CHANGE_EVENT, this.handleOrgPickerPilotChange);
    window.addEventListener('storage', this.handleOrgPickerPilotStorageChange);
    this.refreshOrgPickerEnabled();
  }
  disconnectedCallback() {
    this.headerResizeObserver?.disconnect();
    this.headerResizeObserver = undefined;
    this.headerMutationObserver?.disconnect();
    this.headerMutationObserver = undefined;
    window.removeEventListener('resize', this.publishHeaderHeight);
    window.removeEventListener(ORG_PICKER_PILOT_CHANGE_EVENT, this.handleOrgPickerPilotChange);
    window.removeEventListener('storage', this.handleOrgPickerPilotStorageChange);
    this.headerElement = undefined;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(HeaderV2, {
  wire: {
    setUserContext: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: userStore,
          selector: state => ({
            isAuthenticated: selectIsAuthenticated(state),
            hasOrgSession: state.isGuestUser === false,
            isLoading: state.isLoading,
            userToken: state.userToken,
            tbidProfile: state.tbidProfile
          }),
          equalityFn
        };
      }
    },
    setIsAgentExchange: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: siteConfigStore,
          selector: selectIsAgentExchange
        };
      }
    },
    setOrgPickerFeatureFlag: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: featureFlagsStore,
          selector: state => ({
            flagsResolved: selectFeatureFlagsLoaded(state),
            flagUserToken: state.context?.userId,
            orgPickerFlag: state.featureFlags['ff-org-picker']
          }),
          equalityFn
        };
      }
    },
    setOrgCount: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: orgPickerStore,
          selector: state => Math.max(state.totalCount, state.orgs.length)
        };
      }
    },
    setSelectedOrg: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: orgContextStore,
          selector: selectSelectedOrg
        };
      }
    },
    setCurrentUrl: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: selectUrl
        };
      }
    },
    setLabels: {
      adapter: WireI18n,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          selector: i18n => i18n.header
        };
      }
    },
    setStatusBadgeLabels: {
      adapter: WireI18n,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          selector: i18n => i18n.statusBadge
        };
      }
    },
    setNotificationLabels: {
      adapter: WireI18n,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          selector: i18n => i18n.notifications
        };
      }
    },
    setOrgContextMenuLabels: {
      adapter: WireI18n,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          selector: i18n => i18n.orgContextMenu
        };
      }
    }
  },
  fields: ["expandedHeaderHeight", "activeCollectionMenuId", "activeNavigationItemId", "currentUrl", "isLoggedIn", "isAuthLoading", "hasOrgSession", "userToken", "flagUserToken", "flagsResolved", "isAgentExchange", "tbidProfile", "selectedMobileUtilityPane", "orgPickerEnabled", "orgCount", "selectedOrg", "staleSelectionClearedOrgId", "orgPickerResolved", "handleOrgPickerPilotChange", "handleOrgPickerPilotStorageChange", "publishHeaderHeight"]
});
const __lwc_component_class_internal = _registerComponent(HeaderV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;